import { message } from '@functions/message.js';

/** ハードスキャナ入力とみなすまでの無入力時間（ms） */
export const HARD_SCAN_IDLE_THRESHOLD_MS = 200;

/** ハードスキャナとみなす最小桁数（上限はなし。15桁超は正規化で先頭14桁に切り詰め） */
export const HARD_SCAN_MIN_DIGITS = 8;

/**
 * 全角数字を半角数字に変換する
 * @param {string} value
 * @returns {string}
 */
export function toHalfWidthDigits(value) {
  if (!value) return '';
  return value.replace(/[０-９]/g, (s) =>
    String.fromCharCode(s.charCodeAt(0) - 0xff10 + 0x30)
  );
}

/**
 * キーワード欄の入力変化を監視し、ハードスキャナ入力を検知する。
 * @param {Object} options
 * @param {() => string} options.getKeyword
 * @param {() => void} options.onScanComplete
 * @param {number} [options.minDigits]
 * @param {number} [options.maxDigits]
 * @param {number} [options.idleThresholdMs]
 * @returns {{ onKeywordChange: (newVal: string) => void, cancelPendingScan: () => void, dispose: () => void, isScanDetectionPending: () => boolean }}
 */
export function createHardScannerKeywordWatcher(options) {
  const {
    getKeyword,
    onScanComplete,
    minDigits = HARD_SCAN_MIN_DIGITS,
    maxDigits,
    idleThresholdMs = HARD_SCAN_IDLE_THRESHOLD_MS,
  } = options;

  let idleTimerId = null;
  let prevDigitLength = 0;

  const clearIdleTimer = () => {
    if (idleTimerId !== null) {
      clearTimeout(idleTimerId);
      idleTimerId = null;
    }
  };

  /**
   * 数字のみの列について、以下の場合にのみ idleThresholdMs 後の判定タイマーを開始する。
   * - 先頭1文字が入ったとき（従来のハードスキャナ想定）
   * - 空欄から一括で minDigits 以上が入ったとき（IME確定など）
   */
  const onKeywordChange = (newVal) => {
    if (!newVal) {
      clearIdleTimer();
      prevDigitLength = 0;
      return;
    }

    const normalized = toHalfWidthDigits(newVal);

    if (!/^\d+$/.test(normalized)) {
      clearIdleTimer();
      prevDigitLength = 0;
      return;
    }

    const shouldStartTimer =
      normalized.length === 1 ||
      (prevDigitLength === 0 && normalized.length >= minDigits);

    if (shouldStartTimer) {
      clearIdleTimer();
      idleTimerId = window.setTimeout(() => {
        idleTimerId = null;

        const current = toHalfWidthDigits(getKeyword() || '');
        const withinMax =
          maxDigits === undefined || maxDigits === null
            ? true
            : current.length <= maxDigits;
        if (/^\d+$/.test(current) && current.length >= minDigits && withinMax) {
          onScanComplete();
        }
      }, idleThresholdMs);
    }

    prevDigitLength = normalized.length;
  };

  const cancelPendingScan = () => {
    clearIdleTimer();
    prevDigitLength = 0;
  };

  const dispose = () => {
    clearIdleTimer();
    prevDigitLength = 0;
  };

  const isScanDetectionPending = () => idleTimerId !== null;

  return { onKeywordChange, cancelPendingScan, dispose, isScanDetectionPending };
}

/**
 * バーコード読取の生文字列から数字のみを抽出する（全角数字は半角化）
 * @param {string} raw
 * @returns {string}
 */
export function extractDigitsFromBarcodeRaw(raw) {
  const half = toHalfWidthDigits(raw || '');
  return half.replace(/\D/g, '');
}

/**
 * JAN-13（GTIN-13）のチェックデジットを、先頭12桁から算出する
 * @param {string} first12
 * @returns {string | null}
 */
export function computeJan13CheckDigit(first12) {
  if (!/^\d{12}$/.test(first12)) {
    return null;
  }
  let sum = 0;
  for (let i = 0; i < 12; i += 1) {
    const n = parseInt(first12[i], 10);
    sum += i % 2 === 0 ? n : n * 3;
  }
  return String((10 - (sum % 10)) % 10);
}

/**
 * 13桁JANのチェックデジット検証
 * @param {string} code13
 * @returns {boolean}
 */
export function isValidJan13(code13) {
  if (!/^\d{13}$/.test(code13)) {
    return false;
  }
  const expected = computeJan13CheckDigit(code13.slice(0, 12));
  return expected !== null && expected === code13[12];
}

/**
 * 8〜13桁: 先頭に0を付加して13桁にする（左ゼロ埋め）
 * 16桁以上かつ先頭2文字が「01」: 先頭2文字を除外した14桁
 * 18桁以上かつ先頭4文字が「(01)」: 先頭4文字を除外した14桁
 * 15桁以上: 先頭14桁のみ
 * 14桁はそのまま
 * @param {string} digits 数字のみ
 * @returns {{ ok: true, value: string } | { ok: false, error: string }}
 */
export function normalizeBarcodeDigitSequence(digits) {
  const len = digits.length;
  if (len >= 8 && len <= 13) {
    return { ok: true, value: digits.padStart(13, '0') };
  }
  if (len === 14) {
    return { ok: true, value: digits };
  }
  if (len >= 18 && digits.startsWith('(01)')) {
    return { ok: true, value: digits.slice(4, 18) };
  }
  if (len >= 16 && digits.startsWith('01')) {
    return { ok: true, value: digits.slice(2, 16) };
  }
  if (len >= 15) {
    return { ok: true, value: digits.slice(0, 14) };
  }
  return { ok: false, error: message.error.barcode.invalid_length };
}

/**
 * バーコードを解析し、janCode / dispensingPackagingUnitCode を決定する。
 * 正規化は normalizeBarcodeDigitSequence（8〜13→13桁、15桁以上→先頭14桁）。
 * 14桁は先頭桁により 調剤包装単位 / JAN導出（先頭1・末尾1を除く12桁＋チェックデジット）を分岐。
 *
 * @param {string} raw 入力欄の生文字列
 * @returns {{ ok: true, janCode: string | null, dispensingPackagingUnitCode: string | null } | { ok: false, error: string }}
 */
export function resolveBarcodeParams(raw) {
  const digits = extractDigitsFromBarcodeRaw(raw);
  if (digits.length < 8) {
    return { ok: false, error: message.error.barcode.invalid_length };
  }

  const norm = normalizeBarcodeDigitSequence(digits);
  if (!norm.ok) {
    return { ok: false, error: norm.error };
  }

  const d = norm.value;

  // 13桁: チェックデジット検証
  if (d.length === 13) {
    if (!isValidJan13(d)) {
      return { ok: false, error: message.error.barcode.invalid_check_digit };
    }
    return {
      ok: true,
      janCode: d,
      dispensingPackagingUnitCode: null,
    };
  }

  // 14桁: 先頭桁により 調剤包装単位 / JAN導出（先頭1・末尾1を除く12桁＋チェックデジット）を分岐
  if (d.length === 14) {
    const head = d[0];
    // 先頭「0」: 調剤包装単位コードとしてAPI検索（janCode は null）
    if (head === '0') {
      return {
        ok: true,
        janCode: null,
        dispensingPackagingUnitCode: d,
      };
    }
    // 先頭「1〜9」: 先頭と末尾を除いた12桁からJANを生成（先頭1桁末尾1桁を除く12桁＋算出チェックデジット）
    if (/^[1-9]$/.test(head)) {
      const middle12 = d.slice(1, 13);
      const check = computeJan13CheckDigit(middle12);
      return {
        ok: true,
        janCode: middle12 + check,
        dispensingPackagingUnitCode: null,
      };
    }
    
  }

  return { ok: false, error: message.error.barcode.invalid_length };
}
